// ─────────────────────────────────────────────────────────────────────────────
// deltaGate.ts — OWNER GATE ONE. "Was the call successful?" YES / NO.
//
// PURE. No fetch, no store, no DOM. It turns one click into the typed intent the
// doctrine requires, or into a NAMED REFUSAL. The component renders what this
// returns; it decides nothing itself.
//
// ── THE RULING THAT SHAPES THIS FILE: THERE IS NO GMAIL PLUGIN ON DELTA ────
// Owner ruling 2026-09-08. Nothing can send and nothing can draft into an
// inbox. So the gate:
//   • RECORDS THE BOOKING and moves the stage in the workbook,
//   • CREATES THE CALENDAR EVENT,
//   • and RENDERS THE EMAIL AS TEXT ON THE SCREEN for the owner to copy.
// The email is NOT an effect of the intent. It is a string this module returns
// and the page displays. An effect list naming a send Delta cannot perform would
// be a promise the runtime has no way to keep — and a half-kept promise here is
// a prospect who was told a time nobody wrote down.
//
// ── TWO EFFECTS, ONE EVENT ─────────────────────────────────────────────────
// The booking record and the calendar event are ONE intent, not two calls.
// Two requests can half-succeed and leave a stage moved against a booking with
// no event in the owner's calendar — he then believes he has a consultation at
// a time nothing will remind him of. The doctrine already requires this shape:
// "Rexy decides → approved ActionIntent → n8n may transport/execute".
//
// ── THE VOCABULARY IS THE MANUAL'S, NOT MINE ───────────────────────────────
// `5-Minute Cold-call Transcript.md` §6 declares the domain outright:
//
//     consultation_intent: AGREED | DECLINED | DEFERRED | UNKNOWN
//     "AGREED requires agreed_datetime to be present; a stated intention with
//      no date is DEFERRED."
//
// and its §0D branches read exactly that field: AGREED → create the consultation
// event; DECLINED → the decay ladder. So the gate writes the field the routes
// already read, and a value outside that domain is a value no route can act on.
//
// ── ONE QUESTION, TWO ANSWERS ──────────────────────────────────────────────
// Owner ruling: NO means DECLINED → decay. It is not "no decision" and it does
// not open a follow-up. DEFERRED and follow-up are separate states that arrive
// WITH THE TRANSCRIPT, which is deliberately not being built (PART 13). The gate
// emits AGREED or DECLINED and never the other two — a click cannot tell the
// difference between "they said no" and "they said maybe", and pretending it can
// is how a maybe becomes a dead company.
//
// ── AND THE RECORD SAYS WHO ANSWERED ───────────────────────────────────────
// `answered_by: 'owner_gate'`, beside the eventual `'transcript'`. The gate
// answers the same question the transcript will — but the day the pipeline
// lands, nobody must have to guess which one decided what. A field that both
// generations carry is the only way to judge an old record fairly.
// ─────────────────────────────────────────────────────────────────────────────

export type GateAnswer = 'yes' | 'no';

/** Only the two a click can honestly produce. DEFERRED and UNKNOWN are in the
 *  manual's domain and are NOT in this type — they arrive with the transcript. */
export type GateIntentValue = 'AGREED' | 'DECLINED';

export type GateEffect = 'booking_record' | 'calendar_event';

export interface GateInput {
  companyId: string;
  callId: string;
  answer: GateAnswer;
  /** ISO 8601 with offset, straight from the owner's picker. Delta does not
   *  infer it, does not parse it out of anything and does not guess. */
  agreedDatetime?: string | null;
}

export interface GateIntent {
  intent: 'consultation_booking';
  company_id: string;
  call_id: string;
  consultation_intent: GateIntentValue;
  agreed_datetime: string | null;
  answered_by: 'owner_gate';
  /** Applied together or not at all. Empty on DECLINED — a decline fires
   *  nothing, and the decay ladder is §0D's branch, not an effect of a click. */
  effects: GateEffect[];
}

export interface GateRefusal {
  refused: true;
  ground: string;
  message: string;
}

export type GateResult = GateIntent | GateRefusal;

export function isRefusal(r: GateResult): r is GateRefusal {
  return (r as GateRefusal).refused === true;
}

/** ISO 8601 with an offset or Z. A local-looking string with no offset is a time
 *  in nobody's timezone, and it would reach a calendar as an hour that is right
 *  on one machine and wrong everywhere else. */
const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export function buildGateIntent(input: GateInput): GateResult {
  const companyId = String(input?.companyId || '').trim();
  const callId = String(input?.callId || '').trim();
  if (!companyId) {
    return { refused: true, ground: 'no_company', message: 'the gate was answered with no company selected.' };
  }
  if (!callId) {
    return { refused: true, ground: 'no_call', message: 'the gate needs the call it is answering for.' };
  }
  if (input.answer === 'no') {
    // DECLINED FIRES NOTHING. Not a send, not an event, not a follow-up.
    return {
      intent: 'consultation_booking',
      company_id: companyId,
      call_id: callId,
      consultation_intent: 'DECLINED',
      agreed_datetime: null,
      answered_by: 'owner_gate',
      effects: [],
    };
  }
  if (input.answer !== 'yes') {
    return { refused: true, ground: 'no_answer', message: 'the gate has two answers and neither was given.' };
  }
  const when = String(input.agreedDatetime || '').trim();
  if (!when) {
    // §6's own rule, not a preference of mine: a stated intention with no date
    // is DEFERRED — and DEFERRED is not the gate's to emit. So it refuses
    // rather than booking a consultation nothing can be reminded about.
    return {
      refused: true,
      ground: 'no_datetime',
      message: 'a booking with no date is not a booking — AGREED requires agreed_datetime.',
    };
  }
  if (!ISO_WITH_OFFSET.test(when)) {
    return {
      refused: true,
      ground: 'bad_datetime',
      message: 'the date and time must carry a timezone offset; ' + when + ' does not.',
    };
  }
  return {
    intent: 'consultation_booking',
    company_id: companyId,
    call_id: callId,
    consultation_intent: 'AGREED',
    agreed_datetime: when,
    answered_by: 'owner_gate',
    effects: ['booking_record', 'calendar_event'],
  };
}

// ── THE EMAIL, AS TEXT ─────────────────────────────────────────────────────

export interface EmailInput {
  business: string;
  agreedDatetime: string;
  /** How the owner wants to sign it. Absent renders nothing rather than a
   *  placeholder somebody might send. */
  ownerName?: string | null;
}

/** The confirmation, rendered for the owner to COPY. Nothing here sends it and
 *  nothing here stores it.
 *
 *  A MISSING NAME IS NAMED, NEVER BLANKED. 36 of the qualified companies carry
 *  no business name in words; a greeting with a hole in it is a greeting
 *  somebody pastes into a real email without noticing. The bracket is loud on
 *  purpose. And the slug is never promoted — a slug is an address, not a name. */
export function composeConfirmationEmail(input: EmailInput): string {
  const business = String(input?.business || '').trim();
  const name = business && business !== 'UNKNOWN' ? business : '[no business name recorded]';
  const when = formatAgreed(String(input?.agreedDatetime || ''));
  const sign = String(input?.ownerName || '').trim();
  return [
    'Subject: Confirming our consultation — ' + name,
    '',
    'Hi,',
    '',
    'Thanks for your time on the phone today. Confirming our thirty-minute',
    'consultation for ' + when + '.',
    '',
    'I will send a calendar invitation to this address.',
    '',
    'Speak then,',
    sign || '',
  ].join('\n').replace(/\n+$/, '\n');
}

/** The picker's ISO value in the words an email would use. Returns the raw
 *  string when it cannot be parsed — a date this cannot read is shown as it
 *  was given, never silently replaced with today. */
export function formatAgreed(iso: string): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return iso || '[no date]';
  const day = d.toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return day + ' at ' + time;
}

/**
 * A `datetime-local` value → ISO 8601 WITH THE BROWSER'S OFFSET.
 *
 * THE PICKER HANDS BACK `2026-09-10T14:30` AND NO TIMEZONE. That string is an
 * hour in nobody's timezone: written into a calendar as-is it is right on the
 * machine that typed it and wrong on every other. The owner picks a LOCAL time
 * because that is the time he agreed on the phone, so the offset stamped here is
 * the one his browser is in — supplied, not inferred from anything the prospect
 * said, and never defaulted to UTC.
 *
 * Returns `''` for a value this cannot read, so `buildGateIntent` refuses rather
 * than booking a consultation at a time nobody agreed.
 */
export function localPickerToIso(localValue: string, at?: Date): string {
  const raw = String(localValue || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(raw)) return '';
  const d = at || new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const mins = -d.getTimezoneOffset();
  const sign = mins < 0 ? '-' : '+';
  const abs = Math.abs(mins);
  const pad = (n: number) => String(n).padStart(2, '0');
  const offset = sign + pad(Math.floor(abs / 60)) + ':' + pad(abs % 60);
  const seconds = raw.length === 16 ? ':00' : '';
  return raw + seconds + offset;
}
